import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Copy, Check, Trash2, ExternalLink, Sparkles, QrCode, Download } from "lucide-react";

const SITE_URL = "https://edicolavillaricca.com";
const STORAGE_KEY = "utm_history_v1";

// Preset combos: source + medium + a starter campaign name
const PRESETS = [
  { id: "ig_story",   label: "📸 Storia Instagram",   source: "instagram", medium: "social", campaignHint: "story" },
  { id: "ig_post",    label: "📸 Post Instagram",     source: "instagram", medium: "social", campaignHint: "post" },
  { id: "ig_bio",     label: "🔗 Link in Bio IG",     source: "instagram", medium: "bio",    campaignHint: "bio_link" },
  { id: "fb_post",    label: "📘 Post Facebook",      source: "facebook",  medium: "social", campaignHint: "post" },
  { id: "fb_ads",     label: "💰 Facebook Ads",       source: "facebook",  medium: "cpc",    campaignHint: "fb_ads" },
  { id: "tt_post",    label: "🎵 TikTok",             source: "tiktok",    medium: "social", campaignHint: "post" },
  { id: "yt_video",   label: "▶️ YouTube",            source: "youtube",   medium: "social", campaignHint: "video" },
  { id: "wa_blast",   label: "💬 WhatsApp",           source: "whatsapp",  medium: "messaging", campaignHint: "blast" },
  { id: "tg_channel", label: "✈️ Telegram",           source: "telegram",  medium: "messaging", campaignHint: "channel" },
  { id: "newsletter", label: "📧 Newsletter Email",   source: "newsletter", medium: "email", campaignHint: "newsletter" },
  { id: "google_ads", label: "🔎 Google Ads",         source: "google",    medium: "cpc",    campaignHint: "google_ads" },
  { id: "ebay",       label: "🛒 eBay",               source: "ebay",      medium: "marketplace", campaignHint: "ebay_link" },
  { id: "qr_print",   label: "🖨️ QR / Volantino",     source: "print",     medium: "offline", campaignHint: "volantino" },
];

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 50);

const buildUrl = ({ baseUrl, source, medium, campaign, term, content }) => {
  if (!baseUrl) return "";
  let url;
  try {
    // Allow user to type either full URL or just a path like /prodotto/abc
    if (baseUrl.startsWith("/")) {
      url = new URL(`${SITE_URL}${baseUrl}`);
    } else if (!baseUrl.startsWith("http")) {
      url = new URL(`https://${baseUrl}`);
    } else {
      url = new URL(baseUrl);
    }
  } catch {
    return "";
  }
  if (source) url.searchParams.set("utm_source", slugify(source));
  if (medium) url.searchParams.set("utm_medium", slugify(medium));
  if (campaign) url.searchParams.set("utm_campaign", slugify(campaign));
  if (term) url.searchParams.set("utm_term", slugify(term));
  if (content) url.searchParams.set("utm_content", slugify(content));
  return url.toString();
};

const todayTag = () => {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
};

export const UtmGeneratorTab = () => {
  const [baseUrl, setBaseUrl] = useState(SITE_URL);
  const [source, setSource] = useState("");
  const [medium, setMedium] = useState("");
  const [campaign, setCampaign] = useState("");
  const [term, setTerm] = useState("");
  const [content, setContent] = useState("");
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState([]);

  // Load history
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setHistory(JSON.parse(raw));
    } catch { /* ignore */ }
  }, []);

  const persist = useCallback((next) => {
    setHistory(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  }, []);

  const generatedUrl = useMemo(
    () => buildUrl({ baseUrl, source, medium, campaign, term, content }),
    [baseUrl, source, medium, campaign, term, content]
  );

  const isValid = useMemo(
    () => Boolean(generatedUrl) && Boolean(source) && Boolean(medium) && Boolean(campaign),
    [generatedUrl, source, medium, campaign]
  );

  const applyPreset = (p) => {
    setSource(p.source);
    setMedium(p.medium);
    if (!campaign) {
      setCampaign(`${p.campaignHint}_${todayTag()}`);
    }
  };

  const handleCopy = async () => {
    if (!generatedUrl) return;
    try {
      await navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      // Save to history (dedupe by URL)
      const entry = {
        url: generatedUrl,
        source, medium, campaign, term, content,
        baseUrl,
        savedAt: new Date().toISOString(),
      };
      const next = [entry, ...history.filter((h) => h.url !== generatedUrl)].slice(0, 20);
      persist(next);
    } catch (e) {
      console.error("clipboard failed", e);
    }
  };

  const handleClear = () => {
    setSource(""); setMedium(""); setCampaign(""); setTerm(""); setContent("");
  };

  const handleDeleteEntry = (url) => {
    persist(history.filter((h) => h.url !== url));
  };

  const handleClearHistory = () => {
    if (window.confirm("Cancellare tutta la cronologia link UTM?")) {
      persist([]);
    }
  };

  const qrUrl = useMemo(() => {
    if (!generatedUrl) return "";
    return `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=10&data=${encodeURIComponent(generatedUrl)}`;
  }, [generatedUrl]);

  const downloadQr = async () => {
    if (!qrUrl) return;
    try {
      const res = await fetch(qrUrl);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `qr_${slugify(source)}_${slugify(campaign) || todayTag()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (e) {
      console.error("QR download failed", e);
      window.open(qrUrl, "_blank");
    }
  };

  return (
    <div className="space-y-6" data-testid="utm-generator-tab">
      <div>
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-amber-400" /> Generatore Link UTM
        </h2>
        <p className="text-sm text-gray-400 mt-1">
          Crea link tracciati per Instagram, Facebook, newsletter, eBay, volantini ecc.
          I link generati appariranno nel widget <strong>&quot;Da Dove Arrivano (Referrer)&quot;</strong> della Dashboard.
        </p>
      </div>

      {/* Presets */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-amber-400">
            Preset rapidi
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => applyPreset(p)}
                className="px-3 py-1.5 text-xs rounded-full bg-gray-900/60 border border-gray-700 text-gray-200 hover:border-amber-400 hover:text-amber-300 transition-colors"
                data-testid={`utm-preset-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Form */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base">Parametri del link</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-wide">
                URL di destinazione <span className="text-amber-400">*</span>
              </Label>
              <Input
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder={SITE_URL}
                className="bg-gray-900 border-gray-700 text-white mt-1"
                data-testid="utm-base-url"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Puoi incollare un link prodotto specifico o lasciare la home.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300 text-xs uppercase tracking-wide">
                  Sorgente <span className="text-amber-400">*</span>
                </Label>
                <Input
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  placeholder="instagram"
                  className="bg-gray-900 border-gray-700 text-white mt-1"
                  data-testid="utm-source"
                />
                <p className="text-[11px] text-gray-500 mt-1">utm_source · dove pubblichi</p>
              </div>
              <div>
                <Label className="text-gray-300 text-xs uppercase tracking-wide">
                  Mezzo <span className="text-amber-400">*</span>
                </Label>
                <Input
                  value={medium}
                  onChange={(e) => setMedium(e.target.value)}
                  placeholder="social"
                  className="bg-gray-900 border-gray-700 text-white mt-1"
                  data-testid="utm-medium"
                />
                <p className="text-[11px] text-gray-500 mt-1">utm_medium · tipo (social, cpc, email)</p>
              </div>
            </div>

            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-wide">
                Campagna <span className="text-amber-400">*</span>
              </Label>
              <Input
                value={campaign}
                onChange={(e) => setCampaign(e.target.value)}
                placeholder="saldi_natale_2026"
                className="bg-gray-900 border-gray-700 text-white mt-1"
                data-testid="utm-campaign"
              />
              <p className="text-[11px] text-gray-500 mt-1">utm_campaign · nome leggibile della campagna</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300 text-xs uppercase tracking-wide">Termine (opzionale)</Label>
                <Input
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder="zaino_seven"
                  className="bg-gray-900 border-gray-700 text-white mt-1"
                  data-testid="utm-term"
                />
              </div>
              <div>
                <Label className="text-gray-300 text-xs uppercase tracking-wide">Contenuto (opzionale)</Label>
                <Input
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="banner_a"
                  className="bg-gray-900 border-gray-700 text-white mt-1"
                  data-testid="utm-content"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleClear}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
                data-testid="utm-clear-btn"
              >
                Pulisci
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Preview + QR */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base">Anteprima link</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3 break-all font-mono text-sm text-emerald-300 min-h-[80px]">
              {generatedUrl || (
                <span className="text-gray-500 font-sans">
                  Compila Sorgente, Mezzo e Campagna per generare il link…
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleCopy}
                disabled={!isValid}
                className="bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-40"
                data-testid="utm-copy-btn"
              >
                {copied ? <><Check className="w-4 h-4 mr-1" /> Copiato!</> : <><Copy className="w-4 h-4 mr-1" /> Copia link</>}
              </Button>
              <Button
                asChild
                disabled={!isValid}
                variant="outline"
                className="border-gray-600 text-gray-200 hover:bg-gray-700 disabled:opacity-40"
              >
                <a
                  href={isValid ? generatedUrl : "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="utm-open-btn"
                >
                  <ExternalLink className="w-4 h-4 mr-1" /> Apri
                </a>
              </Button>
              <Button
                onClick={downloadQr}
                disabled={!isValid}
                variant="outline"
                className="border-gray-600 text-gray-200 hover:bg-gray-700 disabled:opacity-40"
                data-testid="utm-qr-download-btn"
              >
                <Download className="w-4 h-4 mr-1" /> QR Code
              </Button>
            </div>

            {isValid && (
              <div className="flex items-center gap-3 pt-2 border-t border-gray-700">
                <div className="bg-white p-2 rounded-lg flex-shrink-0">
                  <img
                    src={qrUrl}
                    alt="QR del link UTM"
                    className="w-28 h-28"
                    data-testid="utm-qr-preview"
                  />
                </div>
                <div className="text-xs text-gray-400">
                  <p className="flex items-center gap-1 font-semibold text-gray-200">
                    <QrCode className="w-3.5 h-3.5" /> QR pronto
                  </p>
                  <p className="mt-1">
                    Stampalo su volantini, biglietti da visita o vetrina.
                    Scaricalo con il bottone &quot;QR Code&quot;.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* History */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-amber-400">
            Cronologia ({history.length})
          </CardTitle>
          {history.length > 0 && (
            <button
              type="button"
              onClick={handleClearHistory}
              className="text-xs text-gray-400 hover:text-red-400 flex items-center gap-1"
              data-testid="utm-clear-history-btn"
            >
              <Trash2 className="w-3 h-3" /> Svuota
            </button>
          )}
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">
              I link copiati appariranno qui. Salvati solo sul tuo browser.
            </p>
          ) : (
            <div className="space-y-2">
              {history.map((h) => (
                <div
                  key={h.url}
                  className="bg-gray-900/60 border border-gray-700 rounded-lg p-3 flex items-center gap-3"
                  data-testid={`utm-history-row-${h.campaign}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="text-sm font-semibold text-amber-300">
                        {h.source} · {h.medium}
                      </span>
                      <span className="text-xs text-gray-400">{h.campaign}</span>
                      <span className="text-[10px] text-gray-500 ml-auto">
                        {new Date(h.savedAt).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 truncate mt-1 font-mono" title={h.url}>
                      {h.url}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(h.url);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 1500);
                      } catch { /* ignore */ }
                    }}
                    className="p-2 text-gray-300 hover:text-amber-300"
                    title="Copia"
                    data-testid={`utm-history-copy-${h.campaign}`}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteEntry(h.url)}
                    className="p-2 text-gray-400 hover:text-red-400"
                    title="Rimuovi"
                    data-testid={`utm-history-delete-${h.campaign}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help */}
      <Card className="bg-amber-950/20 border-amber-700/40">
        <CardContent className="p-4 text-xs text-amber-100/80 space-y-1">
          <p><strong>💡 Come usarli:</strong></p>
          <p>• <strong>utm_source</strong> = dove pubblichi (instagram, facebook, newsletter…)</p>
          <p>• <strong>utm_medium</strong> = tipo di traffico (social, cpc per pubblicità, email…)</p>
          <p>• <strong>utm_campaign</strong> = nome della campagna (saldi_estate_2026, lancio_zaini…)</p>
          <p>• Dopo qualche clic, controlla Dashboard → &quot;Da Dove Arrivano (Referrer)&quot; per vedere il traffico raggruppato per sorgente.</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default UtmGeneratorTab;
